import { CampaignProvider } from './CampaignTour';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '../useMutation';
import { lazy, type ReactNode, Suspense } from 'react';
import type { PortalData } from '../../shared/contracts';
import { failureKindOf, logout, stopImpersonating } from '../api';
import { classNames } from '../classNames';
import {
  isSection,
  parsePath,
  SECTION_TITLES,
  visibleSections,
  type PortalSection,
} from '../../shared/pages';
import { EVENT_COPY } from '../../shared/events';
import { personStanding } from '../../shared/people';
import { sectionIssues } from '../issues';
import { clearPendingCode } from '../pendingCode';
import {
  memberClinicsQuery,
  memberLeagueQuery,
  memberRequirementsQuery,
} from '../queries';
import { refreshMember, switchIdentity } from '../queryClient';
import { navigate, usePath } from '../router';
import { toastFailed } from '../toast';
import { BackLink } from './BackLink';
import { ErrorPanel } from './ErrorPanel';
import { HomeTab } from './HomeTab';
import { SectionQuery } from './SectionQuery';
import { SectionSkeleton } from './SectionSkeleton';
import { EventPosterWall } from './EventPosterWall';
import { HomeLeftColumn } from './HomeLeftColumn';
import { PageShell } from './PageShell';
import { ReportIssueButton } from './ReportIssueButton';
import { SiteHeader } from './SiteHeader';

const WaiversRoute = lazy(() =>
  import('./WaiversPage').then((m) => ({ default: m.WaiversRoute })),
);

const TournamentsRoute = lazy(() =>
  import('./TournamentsPage').then((m) => ({ default: m.TournamentsRoute })),
);
const EventFormsPage = lazy(() =>
  import('./EventFormsPage').then((m) => ({ default: m.EventFormsPage })),
);
const EventsPage = lazy(() =>
  import('./EventsPage').then((m) => ({ default: m.EventsPage })),
);

const AttendanceTab = lazy(() =>
  import('./AttendanceTab').then((m) => ({ default: m.AttendanceTab })),
);
const BobaLeagueTab = lazy(() =>
  import('./BobaLeagueTab').then((m) => ({ default: m.BobaLeagueTab })),
);
const ClinicApplicationPage = lazy(() =>
  import('../clinic/ClinicApplicationPage').then((m) => ({
    default: m.ClinicApplicationPage,
  })),
);
const ClinicsTab = lazy(() =>
  import('./ClinicsTab').then((m) => ({ default: m.ClinicsTab })),
);
const DeclaredSubsRoute = lazy(() =>
  import('./DeclaredSubsPage').then((m) => ({ default: m.DeclaredSubsRoute })),
);
const TeamRegistrationRoute = lazy(() =>
  import('./TeamRegistrationPage').then((m) => ({
    default: m.TeamRegistrationRoute,
  })),
);
const DropInTab = lazy(() =>
  import('./DropInTab').then((m) => ({ default: m.DropInTab })),
);
const ChangeEmailTab = lazy(() =>
  import('./ChangeEmailTab').then((m) => ({ default: m.ChangeEmailTab })),
);
const WatchSurveyRoute = lazy(() =>
  import('./WatchSurvey').then((m) => ({ default: m.WatchSurveyRoute })),
);
const ImpersonateTab = lazy(() =>
  import('./ImpersonateTab').then((m) => ({ default: m.ImpersonateTab })),
);
const TeamReviewPage = lazy(() =>
  import('./TeamReview').then((m) => ({
    default: m.TeamReviewPage,
  })),
);
const ManageDropinsTab = lazy(() =>
  import('./ManageDropinsTab').then((m) => ({ default: m.ManageDropinsTab })),
);
const PersonalInfoTab = lazy(() =>
  import('./PersonalInfoTab').then((m) => ({ default: m.PersonalInfoTab })),
);

function backLink({
  section,
  subsSeason,
  registrationSeason,
  tournamentId,
  eventId,
  waiverDocument,
}: ReturnType<typeof parsePath>): { href: string; label: string } | null {
  if (section === 'home' || section === 'not-found') return null;
  if (section === 'event-forms' && eventId !== null) return null;
  if (section === 'events' && eventId !== null)
    return { href: '/events', label: EVENT_COPY.allEvents };
  if (waiverDocument !== null) return { href: '/waivers', label: 'Waivers' };
  if (subsSeason !== null) {
    return { href: '/league', label: 'Back to your team' };
  }
  if (tournamentId !== null)
    return { href: '/tournaments', label: 'Tournaments' };
  if (registrationSeason !== null) {
    return { href: '/league', label: 'Back to League' };
  }
  return { href: '/', label: 'Back to Home' };
}

function useMemberQueries() {
  return {
    requirements: useQuery(memberRequirementsQuery()),
    league: useQuery(memberLeagueQuery()),
    clinics: useQuery(memberClinicsQuery()),
  };
}

type MemberQueries = ReturnType<typeof useMemberQueries>;

function useSessionActions() {
  const queryClient = useQueryClient();

  const logOut = useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      clearPendingCode();
      void switchIdentity(queryClient);
    },
    onError: (error) =>
      toastFailed(
        failureKindOf(error) === 'network'
          ? 'We could not reach the portal. You are still logged in.'
          : 'We could not log you out. Please try again.',
      ),
  });

  const stopActing = useMutation({
    mutationFn: () => stopImpersonating(),
    onSuccess: () => {
      navigate('/');
      void switchIdentity(queryClient);
    },
    onError: (error) => {
      if (failureKindOf(error) !== 'unauthorized') {
        toastFailed('We could not return to your portal. Please try again.');
      }
    },
  });

  return { logOut, stopActing };
}

function NotFound() {
  return (
    <div className="mx-auto w-full max-w-md">
      <ErrorPanel
        title="Page not found"
        message="That address doesn’t match anything in the portal."
        onRetry={() => navigate('/')}
        retryLabel="Back to home"
      />
    </div>
  );
}

function signerNameOf({ firstName, lastName }: PortalData['profile']) {
  return firstName && lastName ? `${firstName} ${lastName}` : null;
}

function sectionsOf({ viewer, impersonating }: PortalData) {
  return visibleSections(viewer.features, impersonating === null);
}

interface SectionProps {
  data: PortalData;
  path: ReturnType<typeof parsePath>;
  queries: MemberQueries;
}

function SectionContent({
  data,
  path,
  queries,
  issues,
}: SectionProps & { issues: ReturnType<typeof sectionIssues> }) {
  const queryClient = useQueryClient();
  const sections = sectionsOf(data);
  const gate = (section: PortalSection, node: ReactNode) =>
    sections.has(section) ? node : <NotFound />;

  function handleImpersonated() {
    navigate('/');
    void switchIdentity(queryClient);
  }

  switch (path.section) {
    case 'event-forms':
      return gate('event-forms', <EventFormsPage eventId={path.eventId} />);
    case 'events':
      return gate('events', <EventsPage eventId={path.eventId} />);
    case 'home':
      return (
        <HomeTab
          onOpen={(next) => navigate(`/${next}`)}
          issues={issues}
          sections={sections}
        />
      );
    case 'waivers':
      return (
        <WaiversRoute
          documentId={path.waiverDocument}
          signerName={signerNameOf(data.profile)}
        />
      );
    case 'tournaments':
      return (
        <TournamentsRoute
          tournamentId={path.tournamentId}
          signerName={signerNameOf(data.profile)}
          canViewTournaments={sections.has('tournaments')}
        />
      );
    case 'not-found':
      return <NotFound />;
    case 'impersonate':
      return gate(
        'impersonate',
        <ImpersonateTab onImpersonated={handleImpersonated} />,
      );
    case 'change-email':
      return gate('change-email', <ChangeEmailTab />);
    case 'watch-form':
      return gate('watch-form', <WatchSurveyRoute />);
    case 'manage-dropins':
      return gate('manage-dropins', <ManageDropinsTab />);
    case 'team-review':
      return gate('team-review', <TeamReviewPage />);
    case 'attendance':
      return gate('attendance', <AttendanceTab />);
    case 'profile':
      return <ProfileSection data={data} queries={queries} />;
    case 'league':
      return <LeagueSection data={data} path={path} queries={queries} />;
    case 'clinics':
      return (
        <SectionQuery
          query={queries.clinics}
          loadingLabel="Loading your clinics"
        >
          {({ clinics: entries }) => <ClinicsTab entries={entries} />}
        </SectionQuery>
      );
    case 'dropin':
      return <DropinSection data={data} queries={queries} />;
  }
}

function ProfileSection({ data, queries }: Omit<SectionProps, 'path'>) {
  const queryClient = useQueryClient();
  return (
    <SectionQuery
      query={queries.requirements}
      loadingLabel="Loading your profile"
    >
      {({ outstandingRequirements }) => (
        <PersonalInfoTab
          profile={data.profile}
          outstandingRequirements={outstandingRequirements}
          waiversToSign={queries.requirements.data?.waiversToSign ?? []}
          unavailableRequiredWaiverSeason={
            queries.requirements.data?.unavailableRequiredWaiverSeason
          }
          onSaved={() => void refreshMember(queryClient)}
        />
      )}
    </SectionQuery>
  );
}

function LeagueSection({ data, path, queries }: SectionProps) {
  if (path.subsSeason !== null) {
    return <DeclaredSubsRoute season={path.subsSeason} />;
  }
  if (path.registrationSeason !== null) {
    return (
      <TeamRegistrationRoute
        season={path.registrationSeason}
        waiversToSign={queries.requirements.data?.waiversToSign ?? []}
        signerName={signerNameOf(data.profile)}
      />
    );
  }
  return (
    <SectionQuery query={queries.league} loadingLabel="Loading your teams">
      {({ league: entries, currentSeasons }) => (
        <BobaLeagueTab
          entries={entries}
          currentSeasons={currentSeasons}
          teamId={path.teamId}
        />
      )}
    </SectionQuery>
  );
}

function DropinSection({ data, queries }: Omit<SectionProps, 'path'>) {
  return (
    <SectionQuery query={queries.requirements} loadingLabel="Loading drop-ins">
      {({ outstandingRequirements }) => (
        <SectionQuery query={queries.clinics} loadingLabel="Loading drop-ins">
          {({ highestLevel, levelOverride }) => (
            <DropInTab
              disabled={data.impersonating !== null}
              requirementsMet={outstandingRequirements.length === 0}
              onOpenProfile={() => navigate('/profile')}
              highestLevel={highestLevel}
              levelOverride={levelOverride}
            />
          )}
        </SectionQuery>
      )}
    </SectionQuery>
  );
}

export function PortalPage({ data }: { data: PortalData }) {
  const currentPath = usePath();
  const path = parsePath(currentPath);
  const { section } = path;
  const queries = useMemberQueries();
  const { logOut, stopActing } = useSessionActions();

  const issues = sectionIssues({
    requirements: queries.requirements.data,
    league: queries.league.data,
  });

  if (currentPath === '/clinics/register/winter27')
    return (
      <Suspense fallback={<SectionSkeleton />}>
        <ClinicApplicationPage
          canApply={data.viewer.features.includes('clinic_applications')}
        />
      </Suspense>
    );

  const back = backLink(path);

  return (
    <CampaignProvider impersonating={data.impersonating !== null}>
      <PageShell
        label="Your membership"
        left={section === 'home' ? <HomeLeftColumn /> : null}
        right={
          section === 'home' ? (
            <EventPosterWall
              showEvents={sectionsOf(data).has('events')}
              showTournaments={sectionsOf(data).has('tournaments')}
            />
          ) : null
        }
        header={
          <SiteHeader
            title={isSection(section) ? SECTION_TITLES[section] : undefined}
            back={back ?? undefined}
            standing={personStanding(data.person)}
            staffPositions={data.staffPositions}
            issues={issues}
            onHome={() => navigate('/')}
            onLogout={() => logOut.mutate()}
            impersonating={data.impersonating}
            onStopImpersonating={() => stopActing.mutate()}
          />
        }
      >
        <div
          className={classNames(
            'flex flex-col gap-8',
            section === 'attendance' ? null : 'my-auto',
          )}
        >
          <div className="flex flex-col gap-4">
            {back ? (
              <BackLink
                href={back.href}
                label={back.label}
                className="hidden sm:block"
              />
            ) : null}
            <Suspense fallback={<SectionSkeleton />}>
              <SectionContent
                data={data}
                path={path}
                queries={queries}
                issues={issues}
              />
            </Suspense>
          </div>
        </div>
        <ReportIssueButton />
      </PageShell>
    </CampaignProvider>
  );
}
